import { CommonModule } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { ButtonModule } from "primeng/button";
import { NgxExtendedPdfViewerModule } from "ngx-extended-pdf-viewer";
import { environment } from "../../../../../environments/environment";
import {
  DroppedFile,
  GlobalFileDragDropDirective,
} from "../../../../shared/directives/global-file-drag-drop.directive";

interface W9DocumentMetadata {
  id: number;
  fileName: string;
  createdDate: string;
  contentType: string;
}

interface W9ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

@Component({
  selector: "jni-w9-document",
  templateUrl: "./w9-document.component.html",
  styleUrls: ["./w9-document.component.scss"],
  imports: [
    CommonModule,
    ButtonModule,
    NgxExtendedPdfViewerModule,
    GlobalFileDragDropDirective,
  ],
})
export class W9DocumentComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  @ViewChild("w9FileInput")
  private readonly w9FileInput?: ElementRef<HTMLInputElement>;

  @ViewChild("achFileInput")
  private readonly achFileInput?: ElementRef<HTMLInputElement>;

  readonly currentFileName = signal("Checking for the active W-9 document...");
  readonly currentDocumentUrl = signal("");
  readonly currentFileUrl = signal<SafeResourceUrl | null>(null);
  readonly lastUpdatedLabel = signal(
    "Upload a new PDF to replace the current W-9 document."
  );
  readonly isUploading = signal(false);
  readonly activeTabIndex = signal(0);

  readonly currentAchFileName = signal(
    "Checking for the active ACH document..."
  );

  toggleTab(tabIndex: number): void {
    this.activeTabIndex.set(tabIndex);
  }
  readonly currentAchDocumentUrl = signal("");
  readonly currentAchFileUrl = signal<SafeResourceUrl | null>(null);
  readonly achLastUpdatedLabel = signal(
    "Upload a new PDF to replace the current ACH document."
  );
  readonly isAchUploading = signal(false);

  ngOnInit(): void {
    this.loadCurrentDocument();
    this.loadCurrentAchDocument();
  }

  onW9FilesDropped(files: DroppedFile[]): void {
    const file = files?.[0]?.file;
    if (file) {
      this.uploadFile(file);
    }
  }

  onW9FilesRejected(rejections: { file: File; reason: string }[]): void {
    const reason = rejections?.[0]?.reason ?? "Unsupported file selected.";
    this.lastUpdatedLabel.set(reason);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadFile(file);
    }

    if (input) {
      input.value = "";
    }
  }

  triggerFilePicker(): void {
    this.w9FileInput?.nativeElement.click();
  }

  onAchFilesDropped(files: DroppedFile[]): void {
    const file = files?.[0]?.file;
    if (file) {
      this.uploadAchFile(file);
    }
  }

  onAchFilesRejected(rejections: { file: File; reason: string }[]): void {
    const reason = rejections?.[0]?.reason ?? "Unsupported file selected.";
    this.achLastUpdatedLabel.set(reason);
  }

  onAchFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadAchFile(file);
    }

    if (input) {
      input.value = "";
    }
  }

  triggerAchFilePicker(): void {
    this.achFileInput?.nativeElement.click();
  }

  private uploadFile(file: File): void {
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      this.lastUpdatedLabel.set(
        "Only PDF files are supported for the W-9 document."
      );
      return;
    }

    this.isUploading.set(true);
    this.lastUpdatedLabel.set(`Uploading ${file.name}...`);

    const formData = new FormData();
    formData.append("file", file, file.name);

    this.http
      .post<
        W9ApiResponse<W9DocumentMetadata>
      >(`${environment.baseUrl}/W9Documents/upload`, formData)
      .subscribe({
        next: response => {
          const fileName = response?.data?.fileName ?? file.name;
          this.setCurrentDocument(fileName);
          this.lastUpdatedLabel.set(
            `The W-9 document was updated successfully. ${fileName} is now active.`
          );
          this.isUploading.set(false);
        },
        error: () => {
          this.lastUpdatedLabel.set(
            "The W-9 upload could not be completed. Please try again."
          );
          this.isUploading.set(false);
        },
      });
  }

  private loadCurrentDocument(): void {
    this.http
      .get<
        W9ApiResponse<W9DocumentMetadata>
      >(`${environment.baseUrl}/W9Documents/current`)
      .subscribe({
        next: response => {
          const fileName =
            response?.data?.fileName ?? "No W-9 document uploaded";

          if (fileName === "No W-9 document uploaded") {
            this.currentFileName.set(fileName);
            this.currentDocumentUrl.set("");
            this.currentFileUrl.set(null);
            this.lastUpdatedLabel.set("No W-9 document is currently uploaded.");
            return;
          }

          this.setCurrentDocument(fileName);
          this.lastUpdatedLabel.set(
            `${fileName} is currently available for preview and download.`
          );
        },
        error: () => {
          this.currentFileName.set("No W-9 document uploaded");
          this.currentDocumentUrl.set("");
          this.currentFileUrl.set(null);
          this.lastUpdatedLabel.set("No W-9 document is currently uploaded.");
        },
      });
  }

  // The content endpoint requires the MSAL bearer token, which only HttpClient attaches.
  // Iframe/anchor/pdf.js requests are sent without it and get a 401 on deployed environments.
  private setCurrentDocument(fileName: string): void {
    this.http
      .get(`${environment.baseUrl}/W9Documents/current/content`, {
        responseType: "blob",
      })
      .subscribe({
        next: blob => {
          this.revokeUrl(this.currentDocumentUrl());
          const objectUrl = URL.createObjectURL(blob);
          this.currentFileName.set(fileName);
          this.currentDocumentUrl.set(objectUrl);
          this.currentFileUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl)
          );
        },
        error: () => {
          this.currentFileName.set("No W-9 document uploaded");
          this.currentDocumentUrl.set("");
          this.currentFileUrl.set(null);
          this.lastUpdatedLabel.set(
            "The W-9 document could not be loaded. Please try again."
          );
        },
      });
  }

  private revokeUrl(url: string): void {
    if (url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }

  ngOnDestroy(): void {
    this.revokeUrl(this.currentDocumentUrl());
    this.revokeUrl(this.currentAchDocumentUrl());
  }

  downloadDocument(): void {
    const fileName = this.currentFileName();
    const url = this.currentDocumentUrl();

    if (!fileName || !url || fileName === "No W-9 document uploaded") {
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private uploadAchFile(file: File): void {
    if (
      file.type !== "application/pdf" &&
      !file.name.toLowerCase().endsWith(".pdf")
    ) {
      this.achLastUpdatedLabel.set(
        "Only PDF files are supported for the ACH document."
      );
      return;
    }

    this.isAchUploading.set(true);
    this.achLastUpdatedLabel.set(`Uploading ${file.name}...`);

    const formData = new FormData();
    formData.append("file", file, file.name);

    this.http
      .post<
        W9ApiResponse<W9DocumentMetadata>
      >(`${environment.baseUrl}/W9Documents/ach/upload`, formData)
      .subscribe({
        next: response => {
          const fileName = response?.data?.fileName ?? file.name;
          this.setCurrentAchDocument(fileName);
          this.achLastUpdatedLabel.set(
            `The ACH document was updated successfully. ${fileName} is now active.`
          );
          this.isAchUploading.set(false);
        },
        error: () => {
          this.achLastUpdatedLabel.set(
            "The ACH upload could not be completed. Please try again."
          );
          this.isAchUploading.set(false);
        },
      });
  }

  private loadCurrentAchDocument(): void {
    this.http
      .get<
        W9ApiResponse<W9DocumentMetadata>
      >(`${environment.baseUrl}/W9Documents/ach/current`)
      .subscribe({
        next: response => {
          const fileName =
            response?.data?.fileName ?? "No ACH document uploaded";

          if (fileName === "No ACH document uploaded") {
            this.currentAchFileName.set(fileName);
            this.currentAchDocumentUrl.set("");
            this.currentAchFileUrl.set(null);
            this.achLastUpdatedLabel.set(
              "No ACH document is currently uploaded."
            );
            return;
          }

          this.setCurrentAchDocument(fileName);
          this.achLastUpdatedLabel.set(
            `${fileName} is currently available for preview and download.`
          );
        },
        error: () => {
          this.currentAchFileName.set("No ACH document uploaded");
          this.currentAchDocumentUrl.set("");
          this.currentAchFileUrl.set(null);
          this.achLastUpdatedLabel.set(
            "No ACH document is currently uploaded."
          );
        },
      });
  }

  private setCurrentAchDocument(fileName: string): void {
    this.http
      .get(`${environment.baseUrl}/W9Documents/ach/current/content`, {
        responseType: "blob",
      })
      .subscribe({
        next: blob => {
          this.revokeUrl(this.currentAchDocumentUrl());
          const objectUrl = URL.createObjectURL(blob);
          this.currentAchFileName.set(fileName);
          this.currentAchDocumentUrl.set(objectUrl);
          this.currentAchFileUrl.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl)
          );
        },
        error: () => {
          this.currentAchFileName.set("No ACH document uploaded");
          this.currentAchDocumentUrl.set("");
          this.currentAchFileUrl.set(null);
          this.achLastUpdatedLabel.set(
            "The ACH document could not be loaded. Please try again."
          );
        },
      });
  }

  downloadAchDocument(): void {
    const fileName = this.currentAchFileName();
    const url = this.currentAchDocumentUrl();

    if (!fileName || !url || fileName === "No ACH document uploaded") {
      return;
    }

    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}
